import { useState } from 'react';
import { axiosInstance } from 'utils/axios';

// material-ui
import { Box, Button, Grid, Typography, TextField, Select, MenuItem, Autocomplete, CircularProgress, FormControl } from '@mui/material';

import { deepPurple } from '@mui/material/colors';
import { gridSpacing } from 'app/constant';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { errorAlert } from 'ui-component/Toasts';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useFetchData from 'hooks/useFetchData';
import StickyHeadTable from './components/StickyHeadTable';
import * as XLSX from 'xlsx';

const AmbassadorDeposit = () => {
    const token = useSelector(selectCurrentToken);

    // Filter states
    const [dateFrom, setDateFrom] = useState(dayjs().subtract(1, 'days'));
    const [dateTo, setDateTo] = useState(dayjs());
    const [statusID, setStatusID] = useState(0);
    const [referrerCustomerLoginUserName, setReferrerCustomerLoginUserName] = useState('');
    const [refereeCustomerLoginUserName, setRefereeCustomerLoginUserName] = useState('');

    const [ambassadorDepositIncentives, setAmbassadorDepositIncentives] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [filterBtnDisabled, setFilterBtnDisabled] = useState(false);
    const [exportDataListLoading, setExportDataListLoading] = useState(false);

    const fetchAmbassadorDepositIncentives = async () => {
        let fetchURL = `/AmbassadorDepositIncentive/All?`;

        if (dateFrom !== '') {
            fetchURL += `createdDateFrom=${dateFrom.format('YYYY-MM-DD')}`;
        }
        if (dateTo !== '') {
            fetchURL += `&createdDateTo=${dateTo.format('YYYY-MM-DD')}`;
        }
        if (referrerCustomerLoginUserName !== '') {
            fetchURL += `&referrerCustomerLoginUserName=${referrerCustomerLoginUserName}`;
        }
        if (refereeCustomerLoginUserName !== '') {
            fetchURL += `&refereeCustomerLoginUserName=${refereeCustomerLoginUserName}`;
        }
        if (statusID) {
            fetchURL += `&statusID=${statusID}`;
        }

        setLoading(true);
        try {
            const res = await axiosInstance.get(fetchURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setAmbassadorDepositIncentives(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    function convertDataForSheet(data) {
        if (!data || data.length === 0) {
            warningAlert('There is no data to export');
            throw new Error('Data array is empty or undefined.');
        }

        const headers = Object.keys(data[0]);
        const dataArray = [headers];

        data.forEach((item) => {
            const row = headers.map((key) => item[key]);
            dataArray.push(row);
        });

        return XLSX.utils.aoa_to_sheet(dataArray);
    }

    const exportDataAsXlsx = async () => {
        setFilterBtnDisabled(true);
        setTimeout(() => {
            setFilterBtnDisabled(false);
        }, 3000);
        setExportDataListLoading(true);
        
        let fetchURL = `/AmbassadorDepositIncentive/All?`;

        if (dateFrom !== '') {
            fetchURL += `createdDateFrom=${dateFrom.format('YYYY-MM-DD')}`;
        }
        if (dateTo !== '') {
            fetchURL += `&createdDateTo=${dateTo.format('YYYY-MM-DD')}`;
        }
        if (referrerCustomerLoginUserName !== '') {
            fetchURL += `&referrerCustomerLoginUserName=${referrerCustomerLoginUserName}`;
        }
        if (refereeCustomerLoginUserName !== '') {
            fetchURL += `&refereeCustomerLoginUserName=${refereeCustomerLoginUserName}`;
        }
        if (statusID) {
            fetchURL += `&statusID=${statusID}`;
        }

        try {
            const res = await axiosInstance.get(fetchURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                const fileName = `AmbassadorDepositIncentive_${dayjs().format('MM-DD-YYYY')}`;
                const workbook = XLSX.utils.book_new();
                const worksheet = convertDataForSheet(res.data);
                // Adjust column widths
                const headers = Object.keys(res.data[0]);
                const colWidths = headers.map((header) => ({ wch: header.length + 2 }));

                worksheet['!cols'] = colWidths;

                XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
                XLSX.writeFile(workbook, `${fileName}.xlsx`);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setExportDataListLoading(false);
        }
    };

    const { data: status } = useFetchData('/AmbassadorDepositIncentiveStatus/All');
    const { data: customerLoginUserNames } = useFetchData('/Customer/LoginUserNames');

    return (
        <Grid container item xs={12} padding="20px">
            <Grid item container xs={12} spacing={gridSpacing} sx={{ marginBottom: '10px', overflowY: 'auto' }}>
                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Date From:
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={dateFrom}
                                slotProps={{
                                    textField: {
                                        error: false
                                    }
                                }}
                                onChange={(newValue) => setDateFrom(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Date To:
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={dateTo}
                                slotProps={{
                                    textField: {
                                        error: false
                                    }
                                }}
                                onChange={(newValue) => setDateTo(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Status :
                        </Typography>
                        <FormControl fullWidth>
                            <Select value={statusID} onChange={(e) => setStatusID(e.target.value)} sx={{ minWidth: '200px' }}>
                                <MenuItem value={0}>Select Status</MenuItem>
                                {status.map((s) => {
                                    return (
                                        <MenuItem key={s.id} value={s.id}>
                                            {s.name}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Referrer Name :
                        </Typography>

                        <Autocomplete
                            freeSolo
                            value={referrerCustomerLoginUserName}
                            onChange={(event, newValue) => {
                                setReferrerCustomerLoginUserName(newValue);
                            }}
                            inputValue={referrerCustomerLoginUserName}
                            onInputChange={(event, newInputValue) => {
                                setReferrerCustomerLoginUserName(newInputValue);
                            }}
                            options={customerLoginUserNames}
                            sx={{ width: 170 }}
                            renderInput={(params) => <TextField {...params} />}
                            disableClearable
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Referee Name :
                        </Typography>
                        <Autocomplete
                            freeSolo
                            value={refereeCustomerLoginUserName}
                            onChange={(event, newValue) => {
                                setRefereeCustomerLoginUserName(newValue);
                            }}
                            inputValue={refereeCustomerLoginUserName}
                            onInputChange={(event, newInputValue) => {
                                setRefereeCustomerLoginUserName(newInputValue);
                            }}
                            id="controllable-states-demo"
                            options={customerLoginUserNames}
                            sx={{ width: 170 }}
                            renderInput={(params) => <TextField {...params} />}
                            disableClearable
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={12} lg={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'end',
                            alignItems: 'center',
                            gap: '15px',
                            width: '50%',
                            marginLeft: 'auto'
                        }}
                    >
                        <Button
                            variant="contained"
                            sx={{
                                float: 'right',
                                display: 'block',
                                mx: 'auto',
                                width: '150px',
                                backgroundColor: deepPurple[500],
                                marginTop: '20px',
                                marginBottom: '30px'
                            }}
                            onClick={() => fetchAmbassadorDepositIncentives()}
                            disabled={isLoading}
                        >
                            Filter
                        </Button>
                        
                        <Button
                            variant="contained"
                            sx={{
                                display: 'block',
                                mx: 'auto',
                                width: '150px',
                                backgroundColor: deepPurple[500],
                                marginTop: '20px',
                                marginBottom: '20px'
                            }}
                            onClick={() => exportDataAsXlsx()}
                            disabled={filterBtnDisabled}
                        >
                            Export Data
                        </Button>
                    </Box>
                </Grid>

                <Box sx={{ width: '100%', minHeight: '200px' }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <StickyHeadTable
                            ambassadorDepositIncentives={ambassadorDepositIncentives}
                            fetchAmbassadorDepositIncentives={fetchAmbassadorDepositIncentives}
                            status={status}
                        />
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default AmbassadorDeposit;
